<template>
  <div>
    <component
      :is="textboxWhenEmpty ? 'v-combobox' : 'v-autocomplete'"
      v-if="l_visible"
      :data-id="dataId"
      :dense="dense"
      :outlined="outlined"
      :multiple="multiple"
      :placeholder="placeholder"
      :menu-props="menuProps"
      :items="data"
      :hide-no-data="searchFunction != null"
      :search-input.sync="search"
      :value="currentValue"
      :item-text="l_displayMember"
      :item-value="l_valueMember"
      :loading="isLoading || searchLoading"
      :rules="getRules"
      :filter="customFilter"
      @focusout="lostFocus"
      @change="onChange"
      :return-object="returnObject"
      :append-icon="getDisabled ? '' : 'mdi-chevron-down'"
      ref="control"
      :class="{
        validated: displayValidated && !hasError && !errorMessages,
        required: l_required,
        naked,
        readonly: readonly || isDisabled || isLoading,
      }"
      :error-messages="errorMessages"
      :clearable="clearable"
      :disabled="getDisabled"
      :readonly="readonly"
      class="dynamic-input-control"
    >
      <template v-slot:label>
        {{ getLabel }}
        <span v-if="optional" class="optional">(nepovinné)</span>
      </template>
      <template v-slot:append>
        <v-tooltip v-if="!!getTooltip && !hideTooltip" bottom max-width="350">
          <template v-slot:activator="{ on }">
            <v-icon slot="activator" class="btn-help text-body-2 ml-2" v-on="on"
              >mdi-help-circle</v-icon
            >
          </template>
          <span> {{ getTooltip }} </span>
        </v-tooltip>
        <div class="v-input__icon v-input__icon--append" v-if="!getDisabled">
          <v-icon>mdi-chevron-down</v-icon>
        </div>
      </template>
    </component>
  </div>
</template>

<script>
import { dynamicControlMixin } from '../../mixins/dynamicControlMixin';
import { VAutocomplete } from 'vuetify/lib';
import { VCombobox } from 'vuetify/lib';

export default {
  components: {
    VAutocomplete,
    VCombobox,
  },
  mixins: [dynamicControlMixin],
  props: {
    value: [Array, Number, String, Object],
    //refreshDataOn: [Array, String], //id comboxov, na ktorych refresh event combo pocuva
    //refreshDataCondition: String, //nazov premennej, ktore posiela (spolu s hodnotou) combobox navesenemu combu
    valueMember: {
      type: String,
      default: 'id',
    },
    displayMember: {
      type: String,
      default: 'displayMember',
    },
    dense: Boolean,
    outlined: {
      type: Boolean,
      default: true,
    },
    multiple: Boolean,
    menuProps: Object,
    placeholder: String,
    optional: Boolean,
    source: String,
    items: {
      type: Array,
      default: null,
    },
    clearable: Boolean,
    naked: Boolean,
    returnObject: Boolean,
    fulltextFilter: Boolean,
    searchFunction: Function, //funkcia, ktora spusti nejaky search ci uz API metodu alebo daco, vracia Promise s datami kde musi byt prop o hodnote l_displayMember a l_valueMember
    searchMinCharacters: {
      type: Number,
      default: 3,
    },
    setFirstRowAfterLoad: {
      // nastavi do comba prvu hodnotu po loade dat
      type: Boolean,
      default: false,
    },
    textboxWhenEmpty: {
      //ked nenajde hodnotu komba, bude sa spravat ako textbox, cize to co zada user to bude ValueMember
      type: Boolean,
      default: false,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    searchInit: {
      //pri inicializaci sa zavola execSearch, aby sa dotiahol objekt
      type: Boolean,
      default: true,
    },
  },
  data() {
    return {
      data: null,
      SEARCH_DELAY: 500,
      searchLoading: false,
      searchTimeout: null,
      search: null,
      currentValue: this.value,
      //kopie props aby nedochadzalo k mutating props
      l_displayMember: this.displayMember,
      l_valueMember: this.valueMember,
      customDataLoadedEmit: !!this.searchFunction && !!this.searchInit,
    };
  },

  watch: {
    value(val) {
      if (val === this.currentValue) return;
      this.update(val);
    },
    search(val) {
      if (this.searchFunction) {
        if (this.currentValue !== null && this.currentValue !== undefined) {
          if (this.returnObject && val === this.currentValue[this.l_valueMember]) return;
          if (!this.returnObject && val === this.currentValue) return;
        }

        this.currentValue = val;

        if (!val || val.length < this.searchMinCharacters) {
          this.data = [];
          return;
        }

        clearTimeout(this.searchTimeout);
        this.searchLoading = true;
        this.searchTimeout = setTimeout(() => this.execSearch(val), this.SEARCH_DELAY);
      }
    },
  },
  computed:{
    getDisabled(){
      return this.disabled || this.isDisabled || this.isLoading;
    }
  },
  methods: {
    execSearch(val = '', setFirstRow, isDirty = true, isDataLoading) {
      return this.searchFunction(val)
        .then(res => {
          this.searchLoading = false;
          this.data = res.data ? res.data : [];
          //('DATA', this.data);
          if (this.data?.length == 0 && this.textboxWhenEmpty && val) {
            let textRow = {
              valueMember: val,
              displayMember: val,
            };
            this.setDirty(isDirty);
            this.update(this.returnObject ? textRow : val);
          }
          if (this.validateData()) {
            if (this.data?.length > 0 && (this.setFirstRowAfterLoad || setFirstRow)) {
              if (!isDataLoading) this.setDirty(true);
              this.update(this.returnObject ? this.data[0] : this.data[0].valueMember);
            }
          }

          if (isDataLoading) {
            //console.log('emitujem');
            this.$emit('dataLoaded');
          }
        })
        .catch(() => {});
    },
    customFilter(item, queryText, itemText) {
      const qt = queryText
        .toLowerCase()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '');
      const it = itemText
        .toLowerCase()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '');
      if (this.fulltextFilter) return it.includes(qt);
      else return it.startsWith(qt);
    },
    onChange(val) {
      this.setDirty(true);
      this.update(val);
    },
    lostFocus() {
      //(this.data && !this.currentValue, this.currentValue);
      if (this.textboxWhenEmpty) {
        if (this.data && !this.currentValue) {
          this.currentValue = null;
        }
      } else {
        this.$emit('input', this.currentValue);
      }
    },
    validateData() {
      if (this.data?.length > 0) {
        //kontrola ci sedi nazov pre valuemember a displaymember
        if (!Object.prototype.hasOwnProperty.call(this.data[0], this.l_displayMember)) {
          console.warn(
            `Combobox id: '${this.dataId}' ma nespravne definovany displayMember!`,
            this.data[0]
          );
          return false;
        }
        if (!Object.prototype.hasOwnProperty.call(this.data[0], this.l_valueMember)) {
          console.warn(
            `Combobox id: '${this.dataId}' ma nespravne definovany valueMember!`,
            this.data[0]
          );
          return false;
        }
        return true;
      }
      return false;
    },
    readData(config, data) {
      if (this.dataId) {
        if (data) this.data = data;

        if (config.controlValue || config.controlValue === 0) {
          //ak chce druhy krat nacitat to iste, musime to stopnut, lebo vo wizarde by sa tocilo nacitavanie dokola medzi dvoma combacmi
          if (
            this.returnObject &&
            this.currentValue &&
            this.currentValue.valueMember === config.controlValue &&
            this.searchFunction
          )
            return Promise.resolve('');

          let textRow = {
            valueMember: config.controlValue,
            displayMember: config.controlValue,
          };
          this.update(this.returnObject && this.searchFunction ? textRow : config.controlValue);
        } else if (this.data?.length > 0 && this.setFirstRowAfterLoad) {
          this.update(this.returnObject ? this.data[0] : this.data[0].valueMember);
        } else if (this.currentValue) this.currentValue = null;

        if (this.searchFunction && this.searchInit) {
          //console.log('execSearch', this.dataId);
          this.execSearch(config.controlValue, true, false, true);
        }
      } else {
        this.data = null; //res.data;
      }
      if (this.data?.length > 0) {
        //upravim displaymember a valuemember
        if (Object.prototype.hasOwnProperty.call(this.data[0], 'DisplayMember')) {
          this.l_displayMember = 'DisplayMember';
        }

        if (Object.prototype.hasOwnProperty.call(this.data[0], 'ValueMember')) {
          this.l_valueMember = 'ValueMember';
        }
      }
      this.validateData();
    },
    update(newVal) {
      if (newVal === this.currentValue) return;
      let primitiveValue;
      let emitValue = newVal;

      if (this.returnObject) {
        if (newVal !== Object(newVal)) {
          emitValue = this.data?.find(d => d.valueMember === newVal);
          if (emitValue === null && this.textboxWhenEmpty) {
            primitiveValue = newVal.displayMember;
            emitValue = newVal;
          } else {
            primitiveValue = emitValue?.displayMember;
          }
        } else {
          primitiveValue = newVal.displayMember;
        }
      } else {
        primitiveValue = newVal;
      }

      this.currentValue = emitValue;
      this.$emit('input', emitValue);
      return this.saveValue(primitiveValue);
    },
    setValue(val) {
      this.currentValue = val;
      this.$emit('input', val);
      return this.saveValue(val, true);
    },
    refresh() {
      this.update(null, false).then(() => {
        this.loadData();
      });
    },
  },
  beforeMount() {},
  mounted() {
    if (this.items) this.data = this.items;
  },
  created() {
    this.data = [];

    if (!this.dataId && this.data.length === 0) {
      console.warn('Combobox nema zadane dataId ani data source!');
      return;
    }

    if (!this.multiple && Array.isArray(this.value)) {
      console.error(
        `Combobox nie je multiple a inicialna hodnota je pole! Inicialna hodnota v tomto pripade musi byt cislo alebo string.`
      );
      this.valid = false;
      return;
    }
  },
};
</script>
